/**
 * Admin Context
 * Shared state and utilities for admin components
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from '../../lib/axios';
import { toast } from 'sonner';
import { API } from '../../App';

const AdminContext = createContext(null);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children, token }) => {
  // Shared loading states
  const [globalLoading, setGlobalLoading] = useState(false);
  
  // Cache for fetched data (prevents refetching on tab switch)
  const [cache, setCache] = useState({});
  
  // Helper to make authenticated API calls
  const apiCall = useCallback(async (method, endpoint, data = null, options = {}) => {
    try {
      const config = {
        ...options
      };
      
      // Remove leading /api if present since API constant already includes it
      const cleanEndpoint = endpoint.startsWith('/api') ? endpoint.slice(4) : endpoint;
      
      let response;
      if (method === 'get') {
        response = await axios.get(`${API}${cleanEndpoint}`, config);
      } else if (method === 'post') {
        response = await axios.post(`${API}${cleanEndpoint}`, data, config);
      } else if (method === 'put') {
        response = await axios.put(`${API}${cleanEndpoint}`, data, config);
      } else if (method === 'delete') {
        response = await axios.delete(`${API}${cleanEndpoint}`, config);
      }
      
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'An error occurred';
      toast.error(message);
      throw error;
    }
  }, [token]);
  
  // Cache getter/setter
  const getCached = useCallback((key) => cache[key], [cache]);
  
  const setCached = useCallback((key, value) => {
    setCache(prev => ({ ...prev, [key]: value }));
  }, []);
  
  const invalidateCache = useCallback((key) => {
    if (key) {
      setCache(prev => {
        const newCache = { ...prev };
        delete newCache[key];
        return newCache;
      });
    } else {
      setCache({});
    }
  }, []);
  
  // Format date helper
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);
  
  // Check if expired
  const isExpired = useCallback((endDate) => {
    if (!endDate) return false;
    return new Date(endDate) < new Date();
  }, []);
  
  // Get schedule status
  const getScheduleStatus = useCallback((startDate, endDate, active) => {
    if (!active) return { label: 'Inactive', color: 'bg-gray-500' };
    if (isExpired(endDate)) return { label: 'Expired', color: 'bg-red-500' };
    if (!startDate) return { label: 'No schedule', color: 'bg-yellow-500' };
    return { label: 'Active', color: 'bg-green-500' };
  }, [isExpired]);
  
  const value = {
    token,
    apiCall,
    globalLoading,
    setGlobalLoading,
    getCached,
    setCached,
    invalidateCache,
    formatDate,
    isExpired,
    getScheduleStatus
  };
  
  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;
